import { Component, computed, inject, OnInit, Signal } from '@angular/core';
import {
  DialogFooterDirective,
  DialogHeaderTitleDirective,
} from 'common/dialog/dialog-slots.directive';
import { TranslateModule } from '@ngx-translate/core';
import { DialogComponent } from 'common/dialog/dialog.component';
import { CardButtonsComponent } from 'common/card/buttons/card-buttons.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { dirtyErrorStateMatcher, requiredAndNotWhitespaceOnly } from 'common/form/form.service';
import { MatError, MatFormField, MatInput, MatLabel } from '@angular/material/input';
import { EnumService } from 'common/enum/enum.service';
import { ApiV1FindingService, FileAttachmentIntake, FindingStatus, Severity } from 'api';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';
import { NotificationService } from 'common/notification/notification.service';
import { MatOption, MatSelect } from '@angular/material/select';
import { AutofocusDirective } from 'common/form/autofocus.directive';
import { ButtonDirective } from 'common/button/button.directive';

interface FileOption {
  id: number;
  name: string;
}

const NO_FILE_ID = -1;

@Component({
  selector: 'comp-add-finding-dialog',
  templateUrl: 'add-finding-dialog.component.html',
  imports: [
    DialogHeaderTitleDirective,
    TranslateModule,
    DialogComponent,
    DialogFooterDirective,
    CardButtonsComponent,
    ReactiveFormsModule,
    MatError,
    MatFormField,
    MatInput,
    MatLabel,
    MatSelect,
    MatOption,
    AutofocusDirective,
    ButtonDirective,
  ],
})
export class AddFindingDialogComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);
  private readonly enumService = inject(EnumService);
  private readonly findingService = inject(ApiV1FindingService);
  private readonly notificationService = inject(NotificationService);
  private readonly dialogRef = inject(MatDialogRef<AddFindingDialogComponent>);

  readonly data = inject<{ fileAttachments: FileAttachmentIntake[] }>(MAT_DIALOG_DATA);

  form!: FormGroup;

  fileOptions: Signal<FileOption[]> = computed(() => {
    const fileOptions = [];
    fileOptions.push();
    return [
      { id: NO_FILE_ID, name: 'No file' },
      ...this.data.fileAttachments.map((file) => ({ id: file.id, name: file.filename })),
    ];
  });

  protected readonly severityOptions = computed(() =>
    this.enumService.getEnumOptions(Severity, 'model.finding.severity'),
  );

  protected readonly statusOptions = computed(() =>
    this.enumService.getEnumOptions(FindingStatus, 'model.finding.status'),
  );

  ngOnInit() {
    this.form = this.formBuilder.group({
      fileAttachmentId: [this.fileOptions()[0]?.id],
      name: ['', [requiredAndNotWhitespaceOnly, Validators.maxLength(255)]],
      description: [''],
      severity: [this.severityOptions()[0]?.ident],
      status: [this.statusOptions()[0]?.ident],
    });
  }

  async onSubmit() {
    if (this.form.invalid) {
      return;
    }

    const fileAttachmentId = this.form.get('fileAttachmentId')?.value;
    try {
      const findingProperties = {
        name: this.form.get('name')?.value,
        description: this.form.get('description')?.value,
        severity: this.form.get('severity')?.value,
        status: this.form.get('status')?.value,
      };
      const newFinding = await firstValueFrom(
        fileAttachmentId !== NO_FILE_ID
          ? this.findingService.createManualFindingForFileAttachment(
              fileAttachmentId,
              findingProperties,
            )
          : this.findingService.createManualFindingForRequirement(
              Number(this.route.snapshot.queryParams['requirementId']),
              findingProperties,
            ),
      );
      this.dialogRef.close(newFinding);
    } catch (error) {
      this.notificationService.showHttpError(
        error,
        'requirementIntake.details.findings.createError',
      );
    }
  }

  protected onCancel() {
    this.dialogRef.close();
  }

  protected readonly dirtyErrorStateMatcher = dirtyErrorStateMatcher;
}
